import { AppPropertiesNotUsed } from 'constants/Mapping.constants';
import _ from 'lodash';
import type { TObjectType } from 'types/state.interface';
import {
  type MappingConfigBase,
  config as mappingConfig,
} from '@timeedit/registration-shared';
import type { TField } from '@timeedit/types';
import CONSTANTS from '@timeedit/types/lib/constants';

type ValidationMappingStatus =
  | 'valid'
  | 'invalid'
  | 'deprecated'
  | 'duplication'
  | 'optional'
  | 'missingRequiredFields';

export interface ValidationMappingConfig {
  title: string;
  props: MappingConfigBase[keyof MappingConfigBase]['props'];
  id?: number | null;
  extId?: string | null;
  shorthand: string;
  status: ValidationMappingStatus;
  isField: boolean;
  missingMandatory: boolean;
}

interface ValidationMappingApp {
  label: string;
  repos: string[];
  configs: ValidationMappingConfig[];
  valid: boolean;
}

export interface ValidationMapping {
  apps: ValidationMappingApp[];
  valid: boolean;
  message: string;
}

/**
 * Validates mapping configuration between object types and their required configurations
 *
 * This function checks if object types are properly mapped according to the registration
 * shared mapping configuration. It validates both object type groups and app properties,
 * ensuring required fields have proper configuration values set.
 *
 * @param objectTypes - Array of object types from the mapping configuration
 * @param allFields - Array of all available fields to check against
 * @returns ValidationMapping object containing validation results, status, and detailed messages
 */
export function validateMapping(
  objectTypes: TObjectType[],
  allFields: TField[]
) {
  const lf = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });
  const invalidApps: Set<string> = new Set();
  const validation: ValidationMapping = {
    apps: [],
    valid: true,
    message: '',
  };

  const appValidation: ValidationMappingApp = {
    label: 'Registration and Allocate',
    repos: ['te-consume', 'data-manager'],
    configs: [],
    valid: true,
  };

  for (const shorthand in mappingConfig) {
    const config = mappingConfig[shorthand];
    const { props } = config;

    if (AppPropertiesNotUsed.includes(props.appProperty)) {
      continue;
    }

    const objectTypeMappings = objectTypes.filter(
      (obj) => obj.applicationObjectTypeGroup === props.objectTypeGroup
    );

    const title =
      _.get(CONSTANTS, `ObjectTypeGroupProps.${props.objectTypeGroup}.label`) ??
      'Missing ' + props.objectTypeGroup;

    if (objectTypeMappings.length == 0) {
      const configValidation: ValidationMappingConfig = {
        title: `Nothing is mapped to ${title}`,
        props,
        id: undefined,
        shorthand,
        status: config.required ? 'invalid' : 'optional',
        isField: false,
        missingMandatory: false,
      };
      appValidation.configs.push(configValidation);
    }

    for (const objectType of objectTypeMappings) {
      const configValidation: ValidationMappingConfig = {
        title: `${objectType?.applicationObjectTypeLabel} is mapped to ${title}`,
        props,
        id: undefined,
        shorthand,
        status: 'valid',
        isField: false,
        missingMandatory: false,
      };

      let id = objectType?.objectTypeId;
      let extId = objectType?.objectTypeExtId;
      configValidation.isField = false;
      if ('appProperty' in props) {
        const field = objectType?.fields.find(
          (field) => field.appProperty === props.appProperty
        );
        id = field?.fieldId;
        extId = field?.fieldExtId;
        configValidation.isField = true;

        // Check if field has requiredMandatory - can only be checked if something is mapped
        if ('requiredMandatory' in props && isMappingValid(id)) {
          const currentField = allFields.find((f) => f.id === id);
          if (currentField && !currentField.mandatory) {
            configValidation.missingMandatory = true;
            validation.valid = false;
            appValidation.valid = false;
            invalidApps.add(appValidation.label);
          }
        }

        // Check if field has requiredConfiguration - can only be checked if something is mapped
        if ('requiredConfiguration' in props && isMappingValid(id)) {
          const currentField = allFields.find((f) => f.id === id);

          if (currentField) {
            const missingFields = Object.keys(
              props.requiredConfiguration
            ).filter(
              (requiredConf) => !currentField[requiredConf as keyof TField]
            );

            if (missingFields.length > 0) {
              configValidation.status = 'missingRequiredFields';
              validation.valid = false;
              appValidation.valid = false;
              invalidApps.add(appValidation.label);
            }
          }
        }
      }

      const previouslyMapped = extId !== undefined && extId !== null;
      configValidation.id = id;
      configValidation.extId = extId;

      if (!isMappingValid(id) && config.required) {
        validation.valid = false;
        appValidation.valid = false;
        configValidation.status = previouslyMapped ? 'deprecated' : 'invalid';
        invalidApps.add(appValidation.label);
      }

      if (!isMappingValid(id) && !config.required) {
        configValidation.status = 'optional';
      }

      appValidation.configs.push(configValidation);
    }
  }
  validation.apps.push(appValidation);

  validation.message = `Mapping invalid for ${lf.format(
    Array.from(invalidApps)
  )}.`;

  return validation;
}

/**
 * Checks if a mapping ID is valid (not null or undefined)
 * @param id - The ID to validate
 * @returns true if the ID is valid, false otherwise
 */
function isMappingValid(id: number | null | undefined): id is number {
  return id !== undefined && id !== null;
}
